import type { RecaptchaGatedReason } from '@hub/lib/api-error/recaptcha-gated-reason';
import type { FailureEvent, TrackingErrorReason } from '@platform/tracking';
import { type EventMappers, registerGa4Events } from '../../tracking/dataLayerAdapter';

export type LoginMethod = 'email' | 'google' | 'apple' | 'amazon';
export type SsoMethod = Exclude<LoginMethod, 'email'>;

/** How the journey was entered. `session_expired`: an expired token dropped us into it. */
export type AuthSource = 'explicit' | 'guard' | 'session_expired';

/** @deprecated Use {@link AuthSource}; kept while `login_started` still names the journey entry. */
export type LoginSource = AuthSource;

/** SSO login failure reasons. `user_not_found` is the GA4 contract for 404. */
export type LoginFailedReason = 'user_not_found' | 'client_error' | 'server_error' | 'network' | 'timeout' | 'unknown';

/** Steps of the shared identity journey, in the order a user can reach them. */
export type AuthStep = 'identifier' | 'sso_confirm' | 'password' | 'profile' | 'completion' | 'apple_link';

// Where the identifier step sends the user. A rejected address is not a branch: the journey did not
// continue, and `email_check_invalid_domain` (identity/tracking.ts) already reports why.
export type AuthBranch = 'signup' | 'login_password' | 'login_sso';

/** Which account-creating call ran: native register, SSO register, or completion of an existing account. */
export type SignupFlow = 'native' | 'sso' | 'completion';

/** Which signup an event belongs to. Every `signup_*` payload carries it, so one flow reports one shape. */
export type SignupDescriptor = { flow: SignupFlow; method?: LoginMethod };

/**
 * Who verified the address of a completed signup. `email_confirmed`: the user clicked the link in the
 * mail. `provider_confirmed`: the identity provider vouched for it, so the account is created
 * `CONFIRMED_BY_PARTNER` and no confirmation mail is ever sent.
 */
export type SignupVerification = 'email_confirmed' | 'provider_confirmed';

/** Which SSO surface produced the credential. One Tap needs no click, so it has no method-selection step. */
export type SsoSurface = 'button' | 'one_tap';

/** How the step was reached: walking the flow, or pushed onto it by a guard (completion, apple link). */
export type StepOrigin = 'flow' | 'guard';

export type AccountLockedReason = 'contract-terminated' | 'locked';

export type PasswordResetFailedReason = TrackingErrorReason | 'link_expired' | 'insecure_password';

export type AppleLinkFailedReason = TrackingErrorReason | 'already_linked' | 'token_invalid' | 'cancelled';

/** How the code got there. `link`: the TV's deep link carried it, `manual`: the user typed it in. */
export type TvSigninCodeSource = 'link' | 'manual';

/** A union, not a step with an optional field: there is no source to report until a code is in play. */
export type TvSigninView = { step: 'code_entry' } | { step: 'decision'; code_source: TvSigninCodeSource };

/** The answer given to the TV. A `deny` is a deliberate answer, not a failure. */
export type TvSigninDecision = 'allow' | 'deny';

/** Why a code cannot be decided about. A successful read that came back in a non-actionable status. */
export type TvSigninCodeRejection = 'used' | 'expired' | 'unknown' | 'denied';

/**
 * Approval failure reasons; the first three are the BFF problem slugs useDeviceApproval classifies.
 * `approval_forbidden` is the 403 an ineligible account gets. A customer saying no is `tv_signin_decided`.
 */
export type TvSigninFailedReason = TrackingErrorReason | 'approval_forbidden' | 'throttled' | 'not_pending';

/*
 * Event families, canonical for /collect:
 *   auth_*    the shared identity journey, up to and including the login/signup fork
 *   login_*   outcomes of the login branch
 *   signup_*  outcomes of the signup branch
 *   tv_signin_*  device approval, a separate errand that begins once the journey has ended
 * `auth_started` is the journey entry. `login_started` predates it and still means "leaving for the
 * IdP", which is an attempt, not an entry: fold it into `login_attempted` once /kunden-login is gone.
 * `signup_account_created` is deliberately not `signup_completed`: the account exists, but registration
 * is only genuinely complete once its address is confirmed, which may happen minutes later on another
 * device, or already be true at creation time (see SignupVerification).
 */
declare module '@platform/tracking' {
  interface TrackingEvents {
    login_started: { source: LoginSource; target?: string };
    login_attempted: { method: LoginMethod };
    login_completed: { method: LoginMethod };
    login_failed: { method: SsoMethod } & FailureEvent<LoginFailedReason>;
    logout: Record<string, never>;
    logout_failed: { reason: string };

    // Shared journey entry, before the login/signup fork.
    // `entry_context_id` is a registry key and `target` a canonical pathname: no query, no fragment,
    // and no business semantics. What a target means is the owning domain's event to emit, not ours.
    auth_started: { source: AuthSource; entry_context_id?: string; target?: string };
    auth_step_viewed: { step: AuthStep; method?: LoginMethod; origin: StepOrigin };
    auth_identifier_submitted: Record<string, never>;
    auth_branch_resolved: { branch: AuthBranch; provider?: SsoMethod };
    auth_identifier_check_failed: FailureEvent;
    auth_method_selected: { method: SsoMethod; surface: SsoSurface };
    // Not a drop-off guess: the in-memory flow is gone (reload/deep-link) and the guard restarts it.
    auth_flow_restarted: { step: AuthStep };
    // SDK-level outcomes that never reach an endpoint, so no `*_failed` event covers them.
    auth_sso_unavailable: { method: SsoMethod };
    auth_sso_cancelled: { method: SsoMethod };
    auth_sso_error: { method: SsoMethod };

    // Terminal states of the login branch.
    auth_account_locked_viewed: { reason: AccountLockedReason };
    logout_confirmation_viewed: Record<string, never>;
    logout_confirmation_preference_changed: { hidden: boolean };
    logout_confirmation_choice: { choice: 'login' | 'brand_home' };

    // Password reset, the login branch's recovery path.
    password_reset_requested: Record<string, never>;
    password_reset_request_failed: FailureEvent<RecaptchaGatedReason>;
    password_reset_link_invalid: Record<string, never>;
    password_reset_completed: Record<string, never>;
    password_reset_failed: FailureEvent<PasswordResetFailedReason>;

    // Signup branch; the login branch keeps the `login_*` events above.
    signup_sso_confirm_viewed: { method: SsoMethod; has_email: boolean };
    signup_sso_confirmed: { method: SsoMethod };
    signup_sso_cancelled: { method: SsoMethod };
    signup_sso_use_existing: { method: SsoMethod };
    signup_password_submitted: Record<string, never>;
    // No `method` for the completion flow: the session says an account needs completing, not who made it.
    signup_profile_submitted: SignupDescriptor;
    signup_account_created: SignupDescriptor;
    // Terminal signup event, defined by state and not by route: the new account's `doiStatus` reached
    // a confirmed value for the first time. Which flow needs a confirmation mail to get there is the
    // users-service's call, so never derive this from `flow` (see SignupVerification).
    signup_completed: SignupDescriptor & { verification: SignupVerification };
    signup_failed: SignupDescriptor & FailureEvent<RecaptchaGatedReason>;
    signup_apple_link_started: Record<string, never>;
    signup_apple_link_completed: Record<string, never>;
    signup_apple_link_skipped: Record<string, never>;
    signup_apple_link_failed: FailureEvent<AppleLinkFailedReason>;

    // Its own family: `device_*` is device management, and the journey is over before any of this runs.
    tv_signin_viewed: TvSigninView;
    // Mistyped, expired, or already spent. The read succeeded, so it is no failure, just nothing to decide.
    tv_signin_code_rejected: { rejection: TvSigninCodeRejection; code_source: TvSigninCodeSource };
    tv_signin_decided: { decision: TvSigninDecision; code_source: TvSigninCodeSource };
    tv_signin_failed: FailureEvent<TvSigninFailedReason> & { code_source: TvSigninCodeSource };
  }
}

// `auth_*` / `signup_*` / `tv_signin_*` carry no mapper, so they reach /collect only: GA4 delivery is
// opt-in per event (see hubDataLayerAdapter). A mapper here is what projects one into the dataLayer.
// Thin GA4 projection, kept on the existing `login` event name (no GTM trigger change needed).
export const authGa4EventMappers: EventMappers = {
  login_started: (p) => ({ event: 'login', event_description: 'login_started', source: p.source, target: p.target }),
  login_attempted: (p) => ({ event: 'login', event_description: 'login_attempted', method: p.method }),
  login_completed: (p) => ({ event: 'login', event_description: 'login_completed', method: p.method }),
  login_failed: (p) => ({
    event: 'login',
    event_description: 'login_failed',
    method: p.method,
    error_code: p.reason,
    status: p.status,
  }),
  logout: () => ({ event: 'login', event_description: 'logout' }),
  logout_failed: (p) => ({ event: 'login', event_description: 'logout_failed', reason: p.reason }),
};

registerGa4Events(authGa4EventMappers);
